// src/features/merchantConfig/lib/jsonPath.js
//
// Lightweight JSON-path helper. Every leaf rendered by ValueBlockRenderer
// carries its array-of-segments path from the root of the value block.
//
// Phase 1 uses this only for stable React keys + telemetry-friendly logging.
// Phase 2 will use it to build PATCH request bodies (e.g.
// `['business_hours', 'open']` -> `{ business_hours: { open: <new value> } }`)
// without rewriting the renderer.

/**
 * Append a segment to a path immutably.
 *
 * @param {Array<string | number>} parentPath
 * @param {string | number} segment
 * @returns {Array<string | number>}
 */
export function appendPath(parentPath, segment) {
  return [...parentPath, segment];
}

/**
 * Stable string key for React. Distinguishes between numeric array indices
 * and string object keys by wrapping numbers in brackets.
 *
 * @param {Array<string | number>} path
 * @returns {string}
 *
 * @example
 *   pathToKey([])                                  // "$"
 *   pathToKey(["business_hours"])                  // "$.business_hours"
 *   pathToKey(["webhooks", 0, "url"])              // "$.webhooks[0].url"
 */
export function pathToKey(path) {
  if (!path || path.length === 0) return "$";
  return (
    "$" +
    path
      .map((seg) => (typeof seg === "number" ? `[${seg}]` : `.${seg}`))
      .join("")
  );
}

/**
 * Build a nested object that, when merged at the value-block root, sets
 * `value` at `path`. Useful for the Phase 2 PATCH body builder.
 *
 * @param {Array<string | number>} path
 * @param {unknown} value
 * @returns {Record<string, unknown> | unknown[]}
 *
 * @example
 *   buildPatchBody(["business_hours", "open"], "10:00")
 *   // -> { business_hours: { open: "10:00" } }
 */
export function buildPatchBody(path, value) {
  if (!path || path.length === 0) {
    // Replacing the entire value-block root — caller must ensure that's intended.
    return value;
  }
  const root = typeof path[0] === "number" ? [] : {};
  let cursor = root;
  for (let i = 0; i < path.length - 1; i += 1) {
    const seg = path[i];
    const nextSeg = path[i + 1];
    const child = typeof nextSeg === "number" ? [] : {};
    cursor[seg] = child;
    cursor = child;
  }
  cursor[path[path.length - 1]] = value;
  return root;
}

/**
 * Read a value out of a nested object/array using a path. Returns undefined
 * when any segment is missing. Used by the edit-state code to look up draft
 * values without crashing on partial drafts.
 *
 * @param {unknown} root
 * @param {Array<string | number>} path
 * @returns {unknown}
 */
export function getAtPath(root, path) {
  if (!path || path.length === 0) return root;
  let cursor = root;
  for (let i = 0; i < path.length; i += 1) {
    if (cursor === null || cursor === undefined) return undefined;
    if (typeof cursor !== "object") return undefined;
    cursor = cursor[path[i]];
  }
  return cursor;
}

/**
 * Immutably set a value at a path inside a nested object/array. Returns a new
 * root with structural sharing for unaffected branches. If a missing
 * intermediate segment is encountered we synthesize an object (for string
 * segments) or array (for numeric segments) so partial drafts still build.
 *
 * @template T
 * @param {T} root
 * @param {Array<string | number>} path
 * @param {unknown} value
 * @returns {T}
 */
export function setAtPath(root, path, value) {
  if (!path || path.length === 0) {
    // Replace root wholesale.
    // eslint-disable-next-line no-param-reassign
    return /** @type {any} */ (value);
  }

  const [head, ...rest] = path;

  // Clone the current level depending on its expected shape.
  let next;
  if (Array.isArray(root)) {
    next = root.slice();
  } else if (root && typeof root === "object") {
    next = { ...root };
  } else {
    // Initialise an empty container that fits the next segment.
    next = typeof head === "number" ? [] : {};
  }

  if (rest.length === 0) {
    next[head] = value;
  } else {
    const childExisting =
      next[head] !== undefined && next[head] !== null ? next[head] : undefined;
    next[head] = setAtPath(childExisting, rest, value);
  }
  return next;
}

/**
 * Parse a server-side `field_name` string (per OMS API §6 `error_context`) into
 * the path-segment array used by the renderer.
 *
 * Accepted input shapes:
 *   * "opening_hours"               -> ["opening_hours"]
 *   * "business_hours.open"         -> ["business_hours", "open"]
 *   * "webhooks[0].url"             -> ["webhooks", 0, "url"]
 *   * "$.business_hours.open"       -> ["business_hours", "open"]  (jsonPath-style)
 *   * "tiers[3][0]"                 -> ["tiers", 3, 0]
 *
 * Returns `null` if the input is empty / malformed — the caller should fall
 * back to the toolbar banner instead of pinning the error to a row.
 *
 * @param {string | null | undefined} fieldName
 * @returns {Array<string | number> | null}
 */
export function parseFieldNameToPath(fieldName) {
  if (!fieldName || typeof fieldName !== "string") return null;

  // Strip leading "$" / "$." (some clients emit jsonPath-style names).
  let src = fieldName.trim();
  if (src.startsWith("$")) src = src.slice(1);
  if (src.startsWith(".")) src = src.slice(1);
  if (!src) return null;

  const segments = [];
  let token = "";
  let i = 0;
  const flushToken = () => {
    if (token) {
      segments.push(token);
      token = "";
    }
  };

  while (i < src.length) {
    const ch = src[i];
    if (ch === ".") {
      flushToken();
      i += 1;
    } else if (ch === "[") {
      flushToken();
      const close = src.indexOf("]", i);
      if (close === -1) return null;
      const raw = src.slice(i + 1, close).trim();
      // Tolerate quoted string keys (`["foo bar"]`) as well as numeric indices.
      const unquoted = raw.replace(/^['"]|['"]$/g, "");
      if (unquoted !== raw) {
        segments.push(unquoted);
      } else {
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 0) {
          // Treat as a string key (defensive — wire format should be numeric).
          segments.push(raw);
        } else {
          segments.push(n);
        }
      }
      i = close + 1;
    } else {
      token += ch;
      i += 1;
    }
  }
  flushToken();

  return segments.length > 0 ? segments : null;
}

/**
 * Compare two paths for equality (segment-wise).
 *
 * @param {Array<string | number>} a
 * @param {Array<string | number>} b
 * @returns {boolean}
 */
export function pathsEqual(a, b) {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Walk two trees in parallel and produce a minimal patch body — an object
 * containing only the fields whose values differ from the original (per the
 * Jackson "readerForUpdating" semantics documented in MERCHANT_CONFIG_API_DOCS
 * §5.5):
 *   * scalars and arrays that differ → included as-is on the changed branch
 *     (arrays are wholesale replaced)
 *   * objects → recurse, include only the changed subset
 *   * fields removed in the draft are NOT included (server-side `null` is a
 *     no-op; we cannot delete fields via PATCH).
 *
 * Top-level result is always a plain object (the PATCH body shape per
 * API docs §5.4).
 *
 * @param {Record<string, unknown> | null | undefined} original
 * @param {Record<string, unknown> | null | undefined} draft
 * @returns {Record<string, unknown>}
 */
export function diffForPatch(original, draft) {
  const safeOrig = original && typeof original === "object" ? original : {};
  const safeDraft = draft && typeof draft === "object" ? draft : {};
  const out = {};

  for (const key of Object.keys(safeDraft)) {
    const o = safeOrig[key];
    const d = safeDraft[key];
    if (shallowEquals(o, d)) continue;
    if (isPlainObject(o) && isPlainObject(d)) {
      const sub = diffForPatch(o, d);
      // Skip empty sub-diffs (paranoia — shallowEquals should have caught it).
      if (Object.keys(sub).length > 0) out[key] = sub;
    } else {
      // Arrays are wholesale-replaced; scalars overwrite. Either way we
      // include the draft value as-is.
      out[key] = d;
    }
  }

  return out;
}

function isPlainObject(v) {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Cheap deep-equality good enough for the values we receive from the OMS
 * Merchant Config API (plain JSON: strings/numbers/booleans/null + nested
 * objects and arrays). Avoids pulling in lodash just for this.
 */
function shallowEquals(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!shallowEquals(a[i], b[i])) return false;
    }
    return true;
  }
  if (typeof a === "object" && typeof b === "object") {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (const k of ka) {
      if (!shallowEquals(a[k], b[k])) return false;
    }
    return true;
  }
  return false;
}
